/**
 * URL-persisted time range.
 *
 * The URL is the SOURCE OF TRUTH. The value is derived from `useSearchParams()` on
 * every render — there is no shadow `useState` that could drift from the address bar,
 * so back/forward, a pasted link and a hard refresh all land on the same range.
 *
 *   ?range=14d
 *   ?range=custom&from=2026-08-03&to=2026-08-21
 *
 * Pages with more than one independent filter pass a `prefix`, which namespaces all
 * three keys (`summary_range`, `summary_from`, `summary_to`) so two hooks on one page
 * cannot overwrite each other.
 *
 * Writes clone the current params and touch ONLY this hook's keys, so unrelated query
 * state (a tab, a selected row, a search term) survives a range change. History is
 * replaced, not pushed — the precedent set by
 * `communications-v2/overviews/TemplatePerformancePage.tsx`.
 *
 * The returned value is memoised on the URL alone, so its identity is stable between
 * renders. That matters: consumers routinely put it in a `useCallback`/`useEffect`
 * dependency list to trigger a fetch, and an unstable value would fetch forever.
 * `defaultValue` and `presets` are therefore read as of the first render — they are
 * screen constants, not reactive inputs.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  DAY_PRESETS,
  TimeRangePreset,
  TimeRangeToken,
  TimeRangeValue,
  isPresetToken,
  parseRangeParams,
} from './timeRange';

export interface UseTimeRangeParamOptions {
  /** Namespaces the three keys: `prefix_range`, `prefix_from`, `prefix_to`. */
  prefix?: string;
  /**
   * Older param names this screen shipped with (e.g. `['period']`). If the canonical
   * key is absent and a legacy key holds a valid preset token, it is adopted and the
   * URL is rewritten to the canonical key — so old bookmarks keep working exactly
   * once, then heal themselves. Legacy names are NOT prefixed; they are the literal
   * keys as they exist in the wild.
   */
  legacyParams?: string[];
  /** Accepted preset tokens. Defaults to the canonical six. */
  presets?: TimeRangePreset[];
}

export function useTimeRangeParam<T extends string = TimeRangeToken>(
  defaultValue: TimeRangeValue<T>,
  opts: UseTimeRangeParamOptions = {}
): [TimeRangeValue<T>, (next: TimeRangeValue<T>) => void] {
  const { prefix, legacyParams, presets = DAY_PRESETS } = opts;
  const [searchParams, setSearchParams] = useSearchParams();

  const rangeKey = prefix ? `${prefix}_range` : 'range';
  const fromKey = prefix ? `${prefix}_from` : 'from';
  const toKey = prefix ? `${prefix}_to` : 'to';

  // Screen constants, captured once so an inline literal cannot churn the memo below.
  const defaultRef = useRef<TimeRangeValue<T>>(defaultValue);
  const presetsRef = useRef<TimeRangePreset[]>(presets);

  // Stable across renders even when the caller passes an inline array.
  const legacyKeys = (legacyParams ?? []).join(',');

  const rawRange = searchParams.get(rangeKey);
  const rawFrom = searchParams.get(fromKey);
  const rawTo = searchParams.get(toKey);

  /** The legacy key currently standing in for the canonical one, if any. */
  const legacyKey = useMemo(() => {
    if (rawRange !== null || !legacyKeys) return null;
    return (
      legacyKeys
        .split(',')
        .find((key) => isPresetToken(searchParams.get(key), presetsRef.current)) ?? null
    );
  }, [rawRange, legacyKeys, searchParams]);

  const rawLegacyValue = legacyKey ? searchParams.get(legacyKey) : null;

  const value = useMemo(
    () =>
      parseRangeParams<T>(
        rawRange ?? rawLegacyValue,
        rawFrom,
        rawTo,
        defaultRef.current,
        presetsRef.current
      ),
    [rawRange, rawLegacyValue, rawFrom, rawTo]
  );

  // Adopt-and-rewrite: the legacy key is honoured once, then replaced in place.
  useEffect(() => {
    if (!legacyKey || !rawLegacyValue) return;
    const next = new URLSearchParams(searchParams);
    next.delete(legacyKey);
    next.set(rangeKey, rawLegacyValue);
    next.delete(fromKey);
    next.delete(toKey);
    setSearchParams(next, { replace: true });
  }, [legacyKey, rawLegacyValue, searchParams, setSearchParams, rangeKey, fromKey, toKey]);

  const setValue = useCallback(
    (next: TimeRangeValue<T>) => {
      const params = new URLSearchParams(searchParams);
      if (next.type === 'custom') {
        params.set(rangeKey, 'custom');
        params.set(fromKey, next.from);
        params.set(toKey, next.to);
      } else {
        params.set(rangeKey, next.type);
        params.delete(fromKey);
        params.delete(toKey);
      }
      if (legacyKeys) legacyKeys.split(',').forEach((key) => params.delete(key));
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams, rangeKey, fromKey, toKey, legacyKeys]
  );

  return [value, setValue];
}

export default useTimeRangeParam;
